/**
 * The form controls, now on MUI — `Select` is MUI `Autocomplete` (it covers
 * both the searchable and non-searchable cases the hand-rolled version had,
 * plus groups and per-option hint text, without two separate implementations)
 * and `DatePicker` is MUI's `DatePicker` (`@mui/x-date-pickers`, `AdapterMoment`,
 * already provided once at the app root in `main.tsx`).
 *
 * Both keep the EXACT prop signature the hand-rolled versions had —
 * `value`/`onChange`/`options` for `Select`, ISO `yyyy-mm-dd` string in/out
 * for `DatePicker` — so none of this app's ~50 call sites need to change in
 * this wave. What changes is styling: these render as MUI's own components,
 * themed with this app's palette/typography (`theme/MuiTheme.tsx`), not
 * pixel-identical to the old hand-rolled CSS. That trade was already agreed
 * for the date picker specifically; applied the same way here for `Select`,
 * since both controls are replacing a from-scratch reimplementation of
 * something MUI already ships (portal positioning, keyboard nav, search).
 */
import { useId, useState, type ReactNode } from 'react';
import {
	Autocomplete,
	Box,
	Checkbox,
	TextField,
	Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { InfoHint } from './InfoHint';

/* ════════════════════════════════════════════════════════════════════════
   Select
   ════════════════════════════════════════════════════════════════════════ */

export type Option = {
	value: string;
	label: string;
	disabled?: boolean;
	/** Optional heading the option sits under. Options are shown in the order
	 *  given; a group heading is drawn each time the group changes. */
	group?: string;
	/** A second line under the label, saying why this option is being offered.
	 *  The mapping screen uses it to name the spelling it recognised, so a
	 *  column called "Total Gst Value" can be seen to belong to "Tax amount"
	 *  rather than being taken on trust. */
	hint?: string;
};

export function Select({
	id,
	value,
	onChange,
	options,
	placeholder = 'Select',
	disabled,
	invalid,
	ariaLabel,
	className = '',
	emptyLabel = 'Nothing to choose from',
	searchPlaceholder = 'Search…',
}: {
	id?: string;
	value: string;
	onChange: (next: string) => void;
	options: Option[];
	placeholder?: string;
	disabled?: boolean;
	invalid?: boolean;
	ariaLabel?: string;
	className?: string;
	name?: string;
	emptyLabel?: string;
	/** MUI `Autocomplete` always supports typing to filter — kept as a prop for
	 *  call-site compatibility, but no longer changes which component renders. */
	searchable?: boolean;
	searchPlaceholder?: string;
}) {
	const selectedOption =
		options.find((option) => option.value === value) ?? null;
	/* The one input plays two roles the old two-piece component kept separate:
     closed, it shows the current value (or `placeholder`'s hint — a
     recognised-but-unapplied suggestion, say); opened, it becomes a search
     box. Without tracking `open`, `searchPlaceholder` always won and a
     row's suggestion never showed unless something was already selected. */
	const [open, setOpen] = useState(false);

	return (
		<Autocomplete
			id={id}
			className={className}
			options={options}
			value={selectedOption}
			disabled={disabled}
			/* MUI's default: the first click/focus auto-selects the whole
         current value (`useAutocomplete.js`'s `handleClick`, gated on
         `selectOnFocus`) so typing replaces it outright. This field is
         chosen from a list, not typed from scratch, so there is nothing
         to "replace by typing" — the auto-selection only ever showed up
         as a highlighted value with no purpose. Off, so there is nothing
         for any `::selection` rule to ever render here. */
			selectOnFocus={false}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			groupBy={(option) => option.group ?? ''}
			getOptionLabel={(option) => option.label}
			getOptionDisabled={(option) => !!option.disabled}
			isOptionEqualToValue={(option, other) =>
				option.value === other.value
			}
			noOptionsText={emptyLabel}
			size="small"
			onChange={(_event, next) => onChange(next?.value ?? '')}
			renderOption={(props, option) => (
				<li {...props} key={option.value}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '1px',
							minWidth: 0,
						}}
					>
						<span>{option.label}</span>
						{/* Its own line, not run into the label — `.opt-hint`'s legacy
                styling needed an ancestor class (`.sel-opt`) MUI's option
                `<li>` doesn't carry, so this used to render with no line
                break or separator at all. */}
						{option.hint && (
							<Box
								component="span"
								className="opt-hint"
								sx={{ display: 'block' }}
							>
								{option.hint}
							</Box>
						)}
					</Box>
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder={open ? searchPlaceholder : placeholder}
					error={invalid}
					aria-label={ariaLabel}
				/>
			)}
			slotProps={{
				popper: {
					placement: 'bottom-start',
					modifiers: [{ name: 'flip', enabled: false }],
				},
			}}
		/>
	);
}

/* ════════════════════════════════════════════════════════════════════════
   MultiSelect
   ════════════════════════════════════════════════════════════════════════ */

/**
 * Several values from a list: the chosen ones show as chips in the field
 * (the first `limitTags`, then "+N"), and the list opens on click with a
 * checkbox per option, staying open while ticking. Typing filters it.
 */
export function MultiSelect({
	id,
	values,
	onChange,
	options,
	placeholder = 'Select',
	emptyLabel = 'Nothing to choose from',
	limitTags = 2,
}: {
	id?: string;
	values: string[];
	onChange: (next: string[]) => void;
	options: Option[];
	placeholder?: string;
	emptyLabel?: string;
	limitTags?: number;
}) {
	const chosen = options.filter((option) => values.includes(option.value));
	return (
		<Autocomplete
			multiple
			disableCloseOnSelect
			id={id}
			/* Grows with its chips instead of clipping them (the one-line height
         in MuiAutocomplete.ts is for single selects). */
			sx={{
				'& .MuiAutocomplete-inputRoot': {
					height: 'auto !important',
					minHeight: 'var(--ctl-h)',
					flexWrap: 'wrap',
					gap: '4px',
					pt: '4px !important',
					pb: '4px !important',
				},
				'& .MuiAutocomplete-input': {
					height: '26px !important',
					minWidth: '60px !important',
				},
				'& .MuiChip-root': { maxWidth: 220, m: 0 },
			}}
			options={options}
			value={chosen}
			limitTags={limitTags}
			size="small"
			getOptionLabel={(option) => option.label}
			isOptionEqualToValue={(option, other) =>
				option.value === other.value
			}
			noOptionsText={emptyLabel}
			onChange={(_event, next) =>
				onChange(next.map((option) => option.value))
			}
			ChipProps={{ size: 'small' }}
			renderOption={(props, option, { selected }) => (
				<li {...props} key={option.value}>
					<Checkbox
						size="small"
						checked={selected}
						tabIndex={-1}
						sx={{ p: '2px', mr: 1 }}
					/>
					<Typography
						component="span"
						noWrap
						sx={{ flex: 1, minWidth: 0, fontSize: 'inherit' }}
					>
						{option.label}
					</Typography>
					{option.hint && (
						<Typography
							component="span"
							sx={{
								ml: 2,
								flexShrink: 0,
								fontSize: 'var(--fs-small)',
								color: 'var(--ink-3)',
							}}
						>
							{option.hint}
						</Typography>
					)}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder={chosen.length > 0 ? '' : placeholder}
				/>
			)}
		/>
	);
}

/* ════════════════════════════════════════════════════════════════════════
   DatePicker
   ════════════════════════════════════════════════════════════════════════ */

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

/** "Aug 25, 2026" — the format the old hand-rolled picker displayed, kept as
 *  a standalone formatter since screens print it outside any picker too. */
export function formatDisplayDate(value: string | null | undefined): string {
	if (!value) return '';
	const parsed = moment(value, ISO_DATE_FORMAT, true);
	return parsed.isValid() ? parsed.format('MMM D, YYYY') : '';
}

export function DatePicker({
	id,
	value,
	onChange,
	min,
	max,
	disabled,
	invalid,
	placeholder = 'Select a date',
	ariaLabel,
	className = '',
}: {
	id?: string;
	/** ISO yyyy-mm-dd, or '' for none — the same shape the form state and the
	 *  API already use, so no call site has to convert. */
	value: string;
	onChange: (next: string) => void;
	min?: string;
	max?: string;
	disabled?: boolean;
	invalid?: boolean;
	placeholder?: string;
	/** MUI's picker always shows its own clear affordance via the field —
	 *  kept as a prop for call-site compatibility. */
	clearable?: boolean;
	ariaLabel?: string;
	className?: string;
}) {
	return (
		<DesktopDatePicker
			value={value ? moment(value, ISO_DATE_FORMAT) : null}
			onChange={(next) =>
				onChange(
					next && next.isValid() ? next.format(ISO_DATE_FORMAT) : '',
				)
			}
			minDate={min ? moment(min, ISO_DATE_FORMAT) : undefined}
			maxDate={max ? moment(max, ISO_DATE_FORMAT) : undefined}
			disabled={disabled}
			format="MMM D, YYYY"
			slotProps={{
				textField: {
					id,
					className,
					size: 'small',
					error: invalid,
					placeholder,
					'aria-label': ariaLabel,
				},
				popper: {
					placement: 'bottom-start',
					modifiers: [{ name: 'flip', enabled: false }],
				},
			}}
		/>
	);
}

/* ════════════════════════════════════════════════════════════════════════
   A labelled group, so a form does not repeat the same four elements
   ════════════════════════════════════════════════════════════════════════ */

export function Field({
	label,
	hint,
	error,
	required,
	children,
	className = '',
}: {
	label: ReactNode;
	hint?: ReactNode;
	error?: string;
	required?: boolean;
	children: (id: string) => ReactNode;
	className?: string;
}) {
	const id = useId();
	return (
		<div className={`field ${className}`}>
			<div className="fl-row">
				<label className="fl" htmlFor={id}>
					{label}
					{required && (
						<span className="req" aria-hidden="true">
							{' *'}
						</span>
					)}
				</label>
				{/* On the label, not under the control. A hint under every input is
            what turned a four-field form into a page of prose; an error still
            prints in full, because an error has to be read. */}
				{hint && <InfoHint label={`About this field`}>{hint}</InfoHint>}
			</div>
			{children(id)}
			{error && (
				<p className="err" role="alert">
					{error}
				</p>
			)}
		</div>
	);
}
